import cn from "classnames";
import { Clock, MoreCircle, CloseCircle, TickCircle } from "iconsax-react";
import { useTranslation } from "react-i18next";

interface Props {
  status: string;
  isPaymentApproved: boolean;
  isPaid: boolean;
  compact?: boolean;
}

const STAGES = [
  { key: "signed", labelKey: "calculations.pipeline.signed" },
  { key: "approved", labelKey: "calculations.pipeline.approved" },
  { key: "paid", labelKey: "calculations.pipeline.paid" },
] as const;

const SIGNED_STATUSES = new Set(["signed", "approved"]);

function getCompletedCount(status: string, isPaymentApproved: boolean, isPaid: boolean): number {
  if (isPaid) return 3;
  if (isPaymentApproved) return 2;
  if (SIGNED_STATUSES.has(status)) return 1;
  return 0;
}

function getActiveIndex(status: string, isPaymentApproved: boolean, isPaid: boolean): number {
  if (isPaid) return -1;
  if (isPaymentApproved) return 2;
  if (SIGNED_STATUSES.has(status)) return 1;
  if (status === "pending_signing" || status === "signing_pending" || status === "pending_review") return 0;
  return -1;
}

export default function ActStatusPipeline({ status, isPaymentApproved, isPaid, compact = false }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const iconSize = compact ? 12 : 16;

  if (status === "rejected") {
    return (
      <div className="flex items-center flex-wrap gap-1">
        <div className="flex items-center gap-1.5 p-2 rounded-md bg-red-100 text-red-900 dark:text-red-500">
          <CloseCircle size={iconSize} color="currentColor" variant="Bold" className="shrink-0" />
          {!compact && <span className="text-label-sm whitespace-nowrap">{t("calculations.status.rejected")}</span>}
        </div>
      </div>
    );
  }

  const completed = getCompletedCount(status, isPaymentApproved, isPaid);
  const activeIndex = getActiveIndex(status, isPaymentApproved, isPaid);

  return (
    <div className="flex items-center flex-wrap gap-1">
      {STAGES.map((stage, index) => {
        const isCompleted = index < completed;
        const isActive = index === activeIndex;

        return (
          <div key={stage.key} className="flex items-center gap-2">
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-md",
                compact ? "px-1.5 py-0.5" : "p-2",
                isCompleted && "bg-green-100 text-green-900 dark:text-green-500",
                isActive && "bg-orange-100 text-orange-900 dark:text-orange-500",
                !isCompleted && !isActive && "bg-grey-100 text-grey-900 dark:text-grey-500",
              )}>
              {isCompleted ? (
                <TickCircle size={iconSize} color="currentColor" variant="Bold" className="shrink-0" />
              ) : isActive ? (
                <Clock size={iconSize} color="currentColor" className="shrink-0" />
              ) : (
                <MoreCircle size={iconSize} color="currentColor" className="shrink-0" />
              )}
              {!compact && <span className="text-label-sm whitespace-nowrap">{t(stage.labelKey)}</span>}
            </div>
            {index < STAGES.length - 1 && <span className="text-grey-500 text-body-regular-lg">›</span>}
          </div>
        );
      })}
    </div>
  );
}
